/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-06-27 11:39:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { RangeSlider, TextStyle } from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import { Field, change, getFormValues } from 'redux-form';

import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import RenderColorPickerHexPopover from 'src/components/views/common/render-color-picker-hex-popover';

import {
  TEXT_TRANSFORM,
  TEXT_ALIGNMENT,
  DESIGN_STYLE_SETTINGS
} from 'src/constants/recommendation';
import { BUNDLE_DEFAULT_DESIGN_SETTINGS as DEFAULT_SETTINGS } from 'src/constants/bundle';

import { minValue, isRequired, nonXSS } from 'src/helpers/form-validation-helper';
import { normalizeNumberRangeSlider } from 'src/helpers/form-validation-helper';
import { buildOptionList } from 'src/helpers';

import type { RecommendationWidgetType, WidgetDesignSettingsType } from 'src/models/recommendation';

type Props = {
  formValues: RecommendationWidgetType,
  widgetDesignSettings: WidgetDesignSettingsType,
  defaultWidgetSettings: WidgetDesignSettingsType,
  fontList: Array<Object>,
  fontFamily: Array<Object>,
  fontListSuccess: boolean,
  updateField: (field, value) => void
};

type ComposedProps = Props & WithI18nProps;

type State = {
  textTransformOptionList: Array<Object>,
  alignmentOptionList: Array<Object>,
  titleFontStyle: Array<Object>,
  designStyleOptionList: Array<Object>,
  checkDesignStyle: string,
  fontStyleOptionList: Array<Object>,
  customizationStyle: Object
};

const isRequiredValue = isRequired();
const isMinValueSize = minValue(2);
const nonXSSValue = nonXSS();

class SettingsVisualEditor extends Component<ComposedProps, State> {
  state = {
    textTransformOptionList: [],
    alignmentOptionList: [],
    titleFontStyle: [],
    designStyleOptionList: [],
    checkDesignStyle: '',
    fontStyleOptionList: [],
    customizationStyle: {}
  };

  componentDidMount() {
    const { i18n, formValues } = this.props;
    const textTransformOptionList = buildOptionList(TEXT_TRANSFORM, i18n);
    const alignmentOptionList = buildOptionList(TEXT_ALIGNMENT, i18n);
    const designStyleOptionList = buildOptionList(DESIGN_STYLE_SETTINGS, i18n);

    const checkDesignStyle = _.get(formValues, 'templateType', '');

    this.setState({
      textTransformOptionList,
      alignmentOptionList,
      designStyleOptionList,
      checkDesignStyle
    });
  }

  componentWillReceiveProps(nextProps) {
    const { i18n, formValues, fontList } = nextProps;
    const { i18n: prevI18n, formValues: prevFormValues } = this.props;
    const { fontStyleOptionList } = this.state;
    if (i18n.locale !== prevI18n.locale) {
      const alignmentOptionList = buildOptionList(TEXT_ALIGNMENT, i18n);
      const textTransformOptionList = buildOptionList(TEXT_TRANSFORM, nextProps.i18n);
      this.setState({
        alignmentOptionList,
        textTransformOptionList
      });
    }
    // Update design style to show widget title
    if (_.get(formValues, 'templateType', '') !== _.get(prevFormValues, 'templateType', '')) {
      this.setState({
        checkDesignStyle: _.get(formValues, 'templateType', '')
      });
    }

    // Set font style default when first load
    if (
      _.get(this.props, 'fontList.length', 0) !== _.get(nextProps, 'fontList.length', 0) ||
      (fontList.length > 0 && fontStyleOptionList.length === 0)
    ) {
      const titleFont = _.get(formValues, 'titleFont', DEFAULT_SETTINGS.titleFont);
      this.updateFontStyle('fontStyleOptionList', titleFont, true, fontList);
    }
  }
  render() {
    const {
      textTransformOptionList,
      alignmentOptionList,
      // fontStyleOptionList,
      designStyleOptionList,
      checkDesignStyle
    } = this.state;
    const { i18n, updateField, formValues, fontFamily, defaultWidgetSettings } = this.props;
    const titleFontSize = _.get(formValues, 'titleFontSize', DEFAULT_SETTINGS.titleFontSize);
    const titleTextColor = _.get(formValues, 'titleTextColor', DEFAULT_SETTINGS.titleTextColor);
    const buttonBackgroundColor = _.get(
      formValues,
      'buttonBackgroundColor',
      DEFAULT_SETTINGS.buttonBackgroundColor
    );
    const buttonTextColor = _.get(formValues, 'buttonTextColor', DEFAULT_SETTINGS.buttonTextColor);

    return (
      <div className="visual-editor-settings">
        <form>
          <div className="card-section">
            <div className="widget-layout-settings">
              <div className="sub-title">
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.PredictiveBundle.Item.visualEditor_TextSettings_Heading')}
                </TextStyle>
              </div>

              <Field
                name="titleName"
                component={RenderTextField}
                label={i18n.translate('AdminHTML.PredictiveBundle.Item.visualEditor_TitleName_Label')}
                validate={[isRequiredValue, nonXSSValue]}
              />

              <Field
                name="buttonText"
                component={RenderTextField}
                label={i18n.translate('AdminHTML.PredictiveBundle.Item.visualEditor_ButtonText_Label')}
                validate={[isRequiredValue, nonXSSValue]}
              />

              <div className="sub-title">
                <TextStyle variation="strong">
                  {i18n.translate(
                    'AdminHTML.PredictiveBundle.Item.visualEditor_StyleSettings_Heading'
                  )}
                </TextStyle>
              </div>

              {defaultWidgetSettings && (
                <Field
                  name="templateType"
                  label={i18n.translate('AdminHTML.PredictiveBundle.Item.visualEditor_Style_Label')}
                  component={RenderSelect}
                  options={designStyleOptionList}
                  onChange={value => this.onChangeTemplateType(value)}
                />
              )}
              {defaultWidgetSettings && checkDesignStyle === 'customization' && (
                <div className="default-settings-widget" style={{ paddingTop: '8px' }}>
                  {i18n.translate('AdminHTML.PredictiveBundle.Item.visualEditor_Style_Helper')}
                </div>
              )}

              {(!checkDesignStyle || checkDesignStyle === 'customization') && (
                <>
                  <Field
                    name="titleAlignment"
                    label={i18n.translate(
                      'AdminHTML.PredictiveBundle.Item.visualEditor_TitleAlignment_Label'
                    )}
                    component={RenderSelect}
                    options={alignmentOptionList}
                  />
                  <RenderColorPickerHexPopover
                    colorValue={titleTextColor}
                    fieldName="titleTextColor"
                    fieldLabel={i18n.translate(
                      'AdminHTML.PredictiveBundle.Item.visualEditor_TitleTextColor_Label'
                    )}
                    updateField={updateField}
                  />

                  <Field
                    name="titleFont"
                    label={i18n.translate(
                      'AdminHTML.PredictiveBundle.Item.visualEditor_TitleFont_Label'
                    )}
                    component={RenderSelect}
                    onChange={value => this.updateFontStyle('fontStyleOptionList', value)}
                    options={fontFamily}
                  />
                  <Field
                    name="titleTextTransform"
                    label={i18n.translate(
                      'AdminHTML.PredictiveBundle.Item.visualEditor_TitleTextTransform_Label'
                    )}
                    component={RenderSelect}
                    options={textTransformOptionList}
                  />
                  {/* <Field
                    name="titleFontStyle"
                    label={i18n.translate(
                      'AdminHTML.PredictiveBundle.Item.visualEditor_TitleFontStyle_Label'
                    )}
                    component={RenderSelect}
                    options={fontStyleOptionList}
                  /> */}
                  <div className="range-slider-input">
                    <Field
                      name="titleFontSize"
                      label={i18n.translate(
                        'AdminHTML.PredictiveBundle.Item.visualEditor_TitleFontSize_Label'
                      )}
                      component={RenderTextField}
                      onBlur={(event, value) => this.onBlurTextField(event, value, 'titleFontSize')}
                      normalize={(value, prevValue) =>
                        normalizeNumberRangeSlider(value, prevValue, 50)
                      }
                      validate={[isMinValueSize]}
                      anotherProps={{
                        connectedLeft: (
                          <RangeSlider
                            value={titleFontSize}
                            onChange={value => updateField('titleFontSize', value)}
                            max={50}
                            min={2}
                          />
                        )
                      }}
                    />
                  </div>
                  <RenderColorPickerHexPopover
                    colorValue={buttonBackgroundColor}
                    fieldName="buttonBackgroundColor"
                    fieldLabel={i18n.translate(
                      'AdminHTML.PredictiveBundle.Item.visualEditor_ButtonBackgroundColor_Label'
                    )}
                    updateField={updateField}
                  />
                  <RenderColorPickerHexPopover
                    colorValue={buttonTextColor}
                    fieldName="buttonTextColor"
                    fieldLabel={i18n.translate(
                      'AdminHTML.PredictiveBundle.Item.visualEditor_ButtonTextColor_Label'
                    )}
                    updateField={updateField}
                  />
                  <div>
                    <Field
                      name="isDefault"
                      component={RenderCheckbox}
                      label={i18n.translate(
                        'AdminHTML.PredictiveBundle.Item.visualEditor_SaveDefault_Label'
                      )}
                    />
                    <div className="default-settings-widget">
                      {i18n.translate(
                        `AdminHTML.PredictiveBundle.Item.${
                          defaultWidgetSettings
                            ? 'visualEditor_SaveDefault_Helper01'
                            : 'visualEditor_SaveDefault_Helper02'
                        }`
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </form>
      </div>
    );
  }
  // on blur textfield
  onBlurTextField = (event, value, name) => {
    const { updateField } = this.props;
    if (!value) {
      event.preventDefault();
      updateField(name, value);
    }
  };

  // Handle change font
  updateFontStyle = (field, selected, firstLoad = false, fontListNext) => {
    if (!selected) return;

    const { fontList, updateField } = this.props;

    const list = fontList.length === 0 ? fontListNext : fontList;
    const font = _.find(list, font => {
      return font.family === selected;
    });
    if (!font) return;
    const variants = _.get(font, 'variants', []);

    const fontStyleOptionList = variants.map(variant => ({ label: variant, value: variant }));
    this.setState({ [field]: fontStyleOptionList });

    // Auto select first item if not first loading
    if (!firstLoad) {
      updateField('titleFontStyle', fontStyleOptionList[0].value);
    }
  };

  // update isDefault (Save Widget title settings above as Default.) when change templateType
  onChangeTemplateType = value => {
    const { updateField, defaultWidgetSettings, formValues } = this.props;
    updateField('isDefault', value === 'default');
    if (value === 'default') {
      // backup customization title settings before set default title settings
      const {
        titleAlignment = DEFAULT_SETTINGS.titleAlignment,
        titleTextColor = DEFAULT_SETTINGS.titleTextColor,
        titleFont = DEFAULT_SETTINGS.titleFont,
        titleTextTransform = DEFAULT_SETTINGS.titleTextTransform,
        titleFontStyle = DEFAULT_SETTINGS.titleFontStyle,
        titleFontSize = DEFAULT_SETTINGS.titleFontSize,
        buttonColor = DEFAULT_SETTINGS.buttonColor,
        buttonTextColor = DEFAULT_SETTINGS.buttonTextColor
      } = formValues;
      this.setState({
        customizationStyle: {
          titleAlignment,
          titleTextColor,
          titleFont,
          titleTextTransform,
          titleFontStyle,
          titleFontSize,
          buttonColor,
          buttonTextColor
        }
      });
      const defaultSetting = {
        titleAlignment: defaultWidgetSettings.titleAlignment || DEFAULT_SETTINGS.titleAlignment,
        titleTextColor: defaultWidgetSettings.titleTextColor || DEFAULT_SETTINGS.titleTextColor,
        titleFont: defaultWidgetSettings.titleFont || DEFAULT_SETTINGS.titleFont,
        titleTextTransform:
          defaultWidgetSettings.titleTextTransform || DEFAULT_SETTINGS.titleTextTransform,
        titleFontStyle: defaultWidgetSettings.titleFontStyle || DEFAULT_SETTINGS.titleFontStyle,
        titleFontSize: defaultWidgetSettings.titleFontSize || DEFAULT_SETTINGS.titleFontSize,
        buttonColor: defaultWidgetSettings.buttonColor || DEFAULT_SETTINGS.buttonColor,
        buttonTextColor: defaultWidgetSettings.buttonTextColor || DEFAULT_SETTINGS.buttonTextColor
      };
      for (const key of Object.keys(defaultSetting)) {
        updateField(key, defaultSetting[key]);
      }
    } else {
      // revert customization title style
      if (!_.isEmpty(this.state.customizationStyle)) {
        for (const key of Object.keys(this.state.customizationStyle)) {
          updateField(key, this.state.customizationStyle[key]);
        }
      }
    }
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateField: (field, value) => dispatch(change(`BundleVisualEditorForm`, field, value))
});

const mapStateToProps = (state, props) => {
  const formValues = getFormValues('BundleVisualEditorForm')(state);
  const defaultWidgetSettings = _.get(state.recommendation, 'defaultWidgetSettings', {});
  const widgetDesignSettings = _.get(props.widget, 'widgetDesignSettings', {});
  return {
    formValues,
    widgetDesignSettings,
    defaultWidgetSettings,
    fontList: _.get(state, 'font.fontList', []),
    fontFamily: _.get(state, 'font.fontFamily', []),
    fontListSuccess: _.get(state, 'font.fontListSuccess', false)
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SettingsVisualEditor));
